import type { DataGridColumn, DataGridGroupColumn, DataGridLeafColumn } from '@ibsys/kit';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationAttributeItem } from '@/entities/attributes';
import { ATTRIBUTE_TYPES, getAttributeDisplayName } from '@/entities/attributes';
import { useTimeDisplayFormat } from '@/entities/settings';
import {
  type ConversationAttrValue,
  ConversationListItem,
  loadConversationClientOptions,
  loadConversationEmployeeOptions,
  PERSON_ROLES,
  resolveConversationClientOptions,
  resolveConversationEmployeeOptions,
} from '@/entities/conversations';
import {
  getAttrValueByPath,
  getConversationAttrText,
  renderConversationAttrValue,
} from '@/entities/conversations/model/utils';
import type { ExpertReportBlocksTreeMap, ExpertReportResultShapesMap } from '@/entities/expert-report';
import { PhrasePlayButton } from '@/features/phrase-playback';
import { formatHumanDate } from '@/shared/lib/utils/date';
import { formatTimeInterval, withSecondsLabel } from '@/shared/lib/utils/string';
import { getShortName, getTimeRangeFilter } from '@/shared/lib';
import { CopyableCell, TruncatedText, withCopyableCellHover } from '@/shared/ui';
import type { DialogColumnMeta } from './columnMeta';
import { ATTRIBUTE_COLUMN_PREFIX, EMPTY_CELL } from './constants';
import { getAttributeFilterProps, getConversationIdFilterProps } from './dataGridColumnFilters';
import { renderCopyableCell } from './renderCopyableCell';
import { useDialogReportColumns } from './useDialogReportColumns';
import type { ContentSearchColumnOptions } from './useDialogTableColumns';
import {
  getSnippetColumnId,
  getSnippetPlainText,
  getSnippetValue,
  pickContentSearchColumns,
  renderRelevanceCell,
  renderSnippetCell,
} from './contentSearchColumnHelpers';
import { withWrappedText } from './withWrappedText';

/** Параметры хука построения колонок DataGrid для страницы диалогов */
type UseDialogDataGridColumnsParams = {
  /** Все доступные атрибуты — включаются в колонки целиком; видимость управляется снаружи */
  allAttributes: ConversationAttributeItem[];
  /** Все доступные экспертные отчёты — каждый отображается как отдельная группа колонок */
  allReports: { id: number; name: string }[];
  /** Деревья блоков экспертных заключений: reportId → дерево блоков (для вложенных групп колонок) */
  blocksByReportId: ExpertReportBlocksTreeMap;
  /** Чем оценивается итог и блоки видимых заключений — задаёт вид их ячеек и отбора */
  resultShapesByReportId: ExpertReportResultShapesMap;
  /** Колонки «Релевантность» + фрагмент появляются при активном режиме поиска по содержимому диалога */
  contentSearch?: ContentSearchColumnOptions;
};

const getPersonByRole = (
  item: ConversationListItem,
  role: (typeof PERSON_ROLES)[keyof typeof PERSON_ROLES],
): string => {
  const persons = item.persons || [];
  if (persons?.length) {
    const person = persons.find((p) => p.role === role);
    if (person) {
      if (person.fio) return person.fio;
      return getShortName(person.firstName, person.lastName, person.middleName);
    }
  }
  return '';
};

/**
 * Ячейка найденной реплики: копирование её текста и прослушивание самой реплики.
 * Прослушивать нечего, пока неизвестно начало реплики, — тогда остаётся одно копирование.
 */
const renderPhraseSnippetCell = (row: ConversationListItem, content: ReactNode) => (
  <CopyableCell
    text={getSnippetPlainText(row.matchedPhraseSnippet)}
    isContentWrapped
    actions={
      row.matchedPhraseStartedAt == null ? null : (
        <PhrasePlayButton
          conversationId={row.id}
          startedAt={row.matchedPhraseStartedAt}
          finishedAt={row.matchedPhraseFinishedAt ?? null}
        />
      )
    }
  >
    {content}
  </CopyableCell>
);

/** Текст готового содержимого ячейки; у нетекстового значения (чекбокс атрибута) его нет */
const getRenderedText = (rendered: ReactNode): string | null =>
  typeof rendered === 'string' || typeof rendered === 'number' ? String(rendered) : null;

/**
 * Ячейка атрибута: значение любого типа переносится по строкам ширины колонки, а не усекается.
 * В буфер обмена уходит показанный текст; у текстового атрибута, показанного строками и
 * оформлением, — текст без разметки с теми же границами строк.
 */
const renderAttrCell = (attrValue: ConversationAttrValue | undefined, rendered: ReactNode, isText: boolean) =>
  renderCopyableCell(getRenderedText(rendered) ?? (isText ? getConversationAttrText(attrValue) : null), rendered, {
    isContentWrapped: true,
  });

/**
 * Хук построения колонок DataGrid для таблицы диалогов.
 * Все основные колонки всегда включены; атрибуты включаются целиком — видимость управляется снаружи.
 */
export const useDialogDataGridColumns = ({
  allAttributes,
  allReports,
  blocksByReportId,
  resultShapesByReportId,
  contentSearch,
}: UseDialogDataGridColumnsParams): DataGridColumn<ConversationListItem>[] => {
  const { t, i18n } = useTranslation(['dialogs', 'common']);
  const timeDisplayFormat = useTimeDisplayFormat();
  const filterTimeDisplayFormat = useTimeDisplayFormat('filter');

  const mainColumns = useMemo((): DataGridLeafColumn<ConversationListItem>[] => {
    const columns: DataGridLeafColumn<ConversationListItem>[] = [
      {
        id: 'id',
        accessor: 'id',
        header: t('dialogListSection.id'),
        size: 80,
        enableSorting: true,
        enableFiltering: true,
        ...getConversationIdFilterProps(),
        cell: ({ value }) => renderCopyableCell(String(value), <span className="font-roboto">{value as number}</span>),
      },
      {
        id: 'externalId',
        accessor: 'externalId',
        header: t('dialogListSection.externalId'),
        size: 160,
        enableSorting: true,
        enableFiltering: true,
        filter: { type: 'text' },
        cell: ({ value }) => {
          const externalId = value as string | null;
          return renderCopyableCell(externalId, <span className="font-roboto">{externalId ?? EMPTY_CELL}</span>);
        },
      },
      {
        id: 'createdAt',
        accessor: 'createdAt',
        header: t('dialogListSection.date'),
        size: 150,
        enableSorting: true,
        enableFiltering: true,
        filter: { type: 'date-range' },
        cell: ({ value }) => {
          const createdAt = formatHumanDate(i18n, value as string, { withTime: true });
          return renderCopyableCell(createdAt, <span className="font-roboto">{createdAt}</span>);
        },
      },
      {
        id: 'processedAt',
        accessor: 'processedAt',
        header: t('dialogs:finishedAt'),
        size: 170,
        enableSorting: true,
        enableFiltering: true,
        filter: { type: 'date-range' },
        cell: ({ value }) => {
          const finishedAt = value == null ? null : formatHumanDate(i18n, value as string, { withTime: true });
          return renderCopyableCell(finishedAt, <span className="font-roboto">{finishedAt ?? EMPTY_CELL}</span>);
        },
      },
      {
        id: 'duration',
        accessor: 'duration',
        header: withSecondsLabel(t('dialogs:duration'), timeDisplayFormat, t('common:secondsSuffix')),
        size: 150,
        enableSorting: true,
        enableFiltering: true,
        // Длительность — интервал времени, отбор по ней задаётся тем же общим правилом, что и по
        // атрибуту-интервалу: временем показана — границы полем времени, числом секунд — числом
        filter: getTimeRangeFilter(filterTimeDisplayFormat) ?? { type: 'integer' },
        cell: ({ value }) => {
          const duration = value == null ? null : formatTimeInterval(value as number, timeDisplayFormat);
          return renderCopyableCell(duration, <span className="font-roboto">{duration ?? EMPTY_CELL}</span>);
        },
      },
      {
        id: 'client',
        header: t('dialogListSection.client'),
        size: 150,
        enableSorting: true,
        enableFiltering: true,
        // Справочник участников целиком не выгружается: список опций грузится страницами с поиском
        // на бэкенде, а подписи уже выбранных участников догружаются точечной выборкой по их id.
        filter: {
          type: 'multiselect',
          loadOptions: loadConversationClientOptions,
          resolveOptions: resolveConversationClientOptions,
        },
        meta: { personRole: 'client' } satisfies DialogColumnMeta,
        cell: ({ row }) => {
          const client = getPersonByRole(row, PERSON_ROLES.CLIENT);
          return renderCopyableCell(client, <TruncatedText lines={2}>{client}</TruncatedText>);
        },
      },
      {
        id: 'employeeId',
        header: t('dialogListSection.consultant'),
        size: 150,
        enableSorting: true,
        enableFiltering: true,
        filter: {
          type: 'multiselect',
          loadOptions: loadConversationEmployeeOptions,
          resolveOptions: resolveConversationEmployeeOptions,
        },
        meta: { personRole: 'employee' } satisfies DialogColumnMeta,
        cell: ({ row }) => {
          const employee = getPersonByRole(row, PERSON_ROLES.EMPLOYEE);
          return renderCopyableCell(employee, <TruncatedText lines={2}>{employee}</TruncatedText>);
        },
      },
      {
        id: 'hasTranscription',
        accessor: 'hasTranscription',
        header: t('dialogListSection.hasTranscription'),
        size: 160,
        enableSorting: true,
        enableFiltering: true,
        filter: { type: 'boolean' },
        cell: ({ value }) => {
          if (value !== true && value !== false) return <span className="text-muted-foreground">{EMPTY_CELL}</span>;
          const label = value ? t('hasTranscriptionYes') : t('hasTranscriptionNo');
          return renderCopyableCell(label, <span>{label}</span>);
        },
      },
    ];
    return columns.map(withCopyableCellHover);
  }, [t, i18n, timeDisplayFormat, filterTimeDisplayFormat]);

  const reportColumns = useDialogReportColumns({ allReports, blocksByReportId, resultShapesByReportId });

  const attributeColumns = useMemo((): DataGridColumn<ConversationListItem>[] => {
    const ungrouped: DataGridLeafColumn<ConversationListItem>[] = [];
    // code группы → { name, columns }
    const groupedMap = new Map<string, { name: string; columns: DataGridLeafColumn<ConversationListItem>[] }>();

    for (const attr of allAttributes) {
      const isText = attr.dataType === ATTRIBUTE_TYPES.TEXT;
      // id содержит префикс атрибутной колонки — так его ожидает useSmartSearchLazy при фильтрации
      const column: DataGridLeafColumn<ConversationListItem> = {
        id: `${ATTRIBUTE_COLUMN_PREFIX}${attr.path}`,
        header: getAttributeDisplayName(attr, timeDisplayFormat, t('common:secondsSuffix')),
        size: 150,
        enableSorting: true,
        enableFiltering: true,
        ...getAttributeFilterProps(attr, filterTimeDisplayFormat),
        cell: ({ row }) => {
          const attrValue = getAttrValueByPath(row.conversationAttrValues, attr.path);
          const rendered = renderConversationAttrValue(attrValue, attr, timeDisplayFormat);
          return renderAttrCell(attrValue, rendered, isText);
        },
      };
      const leafColumn = withCopyableCellHover(withWrappedText(column));

      // Атрибут группируется по первой группе; без группы — плоский
      const firstGroup = attr.attributeGroups?.[0];
      if (!firstGroup) {
        ungrouped.push(leafColumn);
      } else {
        const existing = groupedMap.get(firstGroup.code);
        if (existing) {
          existing.columns.push(leafColumn);
        } else {
          groupedMap.set(firstGroup.code, { name: firstGroup.name, columns: [leafColumn] });
        }
      }
    }

    const groups: DataGridGroupColumn<ConversationListItem>[] = Array.from(groupedMap.entries()).map(
      ([code, { name, columns }]) => ({
        id: `attrGroup_${code}`,
        header: name,
        panelOnly: true,
        columns,
      }),
    );

    return [...ungrouped, ...groups];
  }, [allAttributes, timeDisplayFormat, filterTimeDisplayFormat, t]);

  const contentSearchColumns = useMemo<DataGridLeafColumn<ConversationListItem>[]>(() => {
    const mode = contentSearch?.mode ?? null;
    if (mode == null) return [];
    const hasQuery = contentSearch?.hasQuery ?? false;
    // Колонки живут только пока активен режим поиска по содержимому, их видимость не сохраняется —
    // крестик в заголовке им не даём, убираются они выключением самого режима поиска.
    const relevance: DataGridLeafColumn<ConversationListItem> = {
      id: 'relevance',
      accessor: 'relevance',
      header: t('dialogs:phraseSearch.relevance'),
      size: 110,
      enableSorting: true,
      enableHiding: false,
      cell: ({ row }) => renderRelevanceCell(row.relevance),
    };
    const snippetId = getSnippetColumnId(mode);
    const snippet: DataGridLeafColumn<ConversationListItem> = {
      id: snippetId,
      accessor: snippetId,
      header: mode === 'phrase' ? t('dialogs:phraseSearch.snippet') : t('dialogs:commentSearch.snippet'),
      size: 240,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        const content = renderSnippetCell(getSnippetValue(row, mode), hasQuery);
        // Фрагмент комментария — перечень комментариев, а не реплика записи: кнопок у него нет
        if (mode !== 'phrase') return content;
        return renderPhraseSnippetCell(row, content);
      },
    };
    // Кнопки ячейки появляются по наведению на неё, а не на сам фрагмент
    const phraseSnippet = mode === 'phrase' ? withCopyableCellHover(snippet) : snippet;
    return pickContentSearchColumns({ relevance, snippet: phraseSnippet }, hasQuery);
  }, [contentSearch?.mode, contentSearch?.hasQuery, t]);

  return useMemo(
    () => [...contentSearchColumns, ...mainColumns, ...reportColumns, ...attributeColumns],
    [contentSearchColumns, mainColumns, reportColumns, attributeColumns],
  );
};
